import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, input, OnInit, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormField, MatHint, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import {
  AppType,
  Charity,
  ENVIRONMENT,
  Environment,
  SPLIT_CARD_DEFAULT_OPTIONS,
  SPLIT_CARD_PRIORITY_DEFAULT_OPTIONS,
  SplitCardFontSize,
  SplitScreen,
  SplitScreenSection,
  SplitScreenSectionsTypes,
  SplitScreenVersion,
} from '@domains';
import {
  CardComponent,
  FxFlexDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  PhotoUploadComponent,
  ResolveUrlPipe,
  Responsive,
  ResponsiveService,
} from '@rspl-ui';
import { ColorPickerModule } from 'ngx-color-picker';

type fieldPair = [string, (value: boolean | SplitScreenVersion) => void];

@Component({
  selector: 'app-split-screen-form',
  templateUrl: './split-screen-form.component.html',
  styleUrls: ['./split-screen-form.component.scss'],
  imports: [
    FxLayoutDirective,
    FxFlexDirective,
    MatIcon,
    FxLayoutGapDirective,
    FormsModule,
    ReactiveFormsModule,
    CardComponent,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    PhotoUploadComponent,
    MatInput,
    MatHint,
    MatTabGroup,
    MatTab,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatPrefix,
    ColorPickerModule,
    MatSlider,
    MatSliderThumb,
    MatTabContent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CommonModule,
    ResolveUrlPipe,
    MatIconButton,
  ],
})
export class SplitScreenFormComponent extends Responsive implements OnInit {
  config = input<SplitScreen>(null);
  charity = input<Charity>(null);

  formChange = output<SplitScreen>();

  enabled = signal(false);
  expandedSections = signal<Set<string>>(new Set());

  form: FormGroup;
  isCaptain: boolean;
  splitScreenVersions = SplitScreenVersion;
  splitscreensectionTypes = SplitScreenSectionsTypes;
  versions = [
    { value: SplitScreenVersion.V1, text: 'Design v1' },
    { value: SplitScreenVersion.V2, text: 'Design v2' },
    { value: SplitScreenVersion.V3A, text: 'Design v3A' },
    { value: SplitScreenVersion.V3B, text: 'Design v3B' },
    { value: SplitScreenVersion.V4, text: 'Design v4' },
  ];
  fontSizes = [
    { value: SplitCardFontSize.small, text: 'Small' },
    { value: SplitCardFontSize.medium, text: 'Medium' },
    { value: SplitCardFontSize.large, text: 'Large' },
  ];

  constructor(
    override responsiveService: ResponsiveService,
    @Inject(ENVIRONMENT) environment: Environment,
  ) {
    super(responsiveService);
    this.isCaptain = environment.app === AppType.CAPTAIN;

    const formBuilder = new FormBuilder();

    this.form = formBuilder.group({
      enabled: [false],
      version: [SplitScreenVersion.V2],
      title: [''],
      text: [''],
      button: ['', [Validators.required]],
      url: ['', [Validators.required]],
      backgroundColor: ['', [Validators.required]],
      textColor: ['', [Validators.required]],
      btnBackgroundColor: ['', [Validators.required]],
      btnTextColor: ['', [Validators.required]],
      titleBackgroundColor: ['', [Validators.required]],
      titleTextColor: ['', [Validators.required]],
      logoBackgroundColor: ['', [Validators.required]],
      stripeBackgroundColor: ['', [Validators.required]],
      bulletColor: ['', [Validators.required]],
      primaryReady: [false],

      hyperlinkUrl: [''],
      hyperlinkDisplayText: [''],

      standardEnabled: [false],
      showRestrictedItems: [false],
      restrictedItemsUrl: ['', [Validators.required]],
      restrictedItemsText: ['', [Validators.required]],
      titlePrimary: [''],
      textPrimary: [''],
      buttonPrimary: [''],
      backgroundColorPrimary: ['', [Validators.required]],
      textColorPrimary: ['', [Validators.required]],
      btnBackgroundColorPrimary: ['', [Validators.required]],
      btnTextColorPrimary: ['', [Validators.required]],
      titleBackgroundColorPrimary: ['', [Validators.required]],
      titleTextColorPrimary: ['', [Validators.required]],
      logoBackgroundColorPrimary: ['', [Validators.required]],
      bulletColorPrimary: ['', [Validators.required]],
      standardReady: [false],

      reverse: [false],
      discounts: [false],
      uber: [false],
      sections: <SplitScreenSection[]>[],
      heroVisible: [true],

      logo: [''],
      logoOffsetTop: [0],
      logoOffsetLeft: [0],
      logoSize: [0],
      logoOffsetTopMobile: [0],
      logoOffsetLeftMobile: [0],
      logoSizeMobile: [0],
      logoStandardOffsetTop: [0],
      logoStandardOffsetLeft: [0],
      logoStandardSize: [0],
      logoStandardOffsetTopMobile: [0],
      logoStandardOffsetLeftMobile: [0],
      logoStandardSizeMobile: [0],
    });
  }

  get isV1orV2() {
    return [SplitScreenVersion.V1, SplitScreenVersion.V2].includes(this.form.get('version').value);
  }

  get isV1() {
    return SplitScreenVersion.V1 === this.form.get('version').value;
  }

  get isFreeSectionVisible() {
    return this.form.get('sections').value.find((section: SplitScreenSection) => section.type === this.splitscreensectionTypes.freePickups)
      .isVisible;
  }

  override ngOnInit(): void {
    this.enabled.set(this.config()?.enabled);
    this.form.patchValue({ ...this.config() });

    this.form.get('enabled').value ? this.form.enable() : this.form.disable();

    this.subscribeToChange([
      ['standardEnabled', this.onStandardEnabledChange.bind(this)],
      ['showRestrictedItems', this.onShowRestrictedItemsChange.bind(this)],
      ['primaryReady', this.onPrimaryReadyChange.bind(this)],
      ['standardReady', this.onStandardReadyChange.bind(this)],
      ['version', this.onVersionChange.bind(this)],
    ]);

    this.onVersionChange(this.form.get('version').value);

    this.form.valueChanges.subscribe((value) => {
      this.formChange.emit({ ...this.form.getRawValue(), ...value });
    });
  }

  private subscribeToChange(fields: fieldPair[]) {
    fields.forEach(([field, onFieldChange]) => {
      this.form.get(field).valueChanges.subscribe((value) => onFieldChange(value));
    });
  }

  private setControlState(controlNames: string[], enabled: boolean): void {
    controlNames.forEach((name) => {
      const control = this.form.get(name);
      if (control) {
        enabled ? control.enable() : control.disable();
      }
    });
  }

  private onStandardEnabledChange(enabled: boolean): void {
    this.setControlState(
      [
        'url',
        'button',
        'title',
        'text',
        'logo',
        'backgroundColor',
        'textColor',
        'btnBackgroundColor',
        'btnTextColor',
        'titleBackgroundColor',
        'titleTextColor',
        'logoBackgroundColor',
        'bulletColor',
        'showRestrictedItems',
        'standardReady',
      ],
      enabled && this.form.get('enabled').value,
    );
  }

  private onPrimaryReadyChange(primaryReady: boolean): void {
    this.setControlState(['buttonPrimary'], primaryReady && this.isV1orV2 && this.form.get('enabled').value);
  }

  private onShowRestrictedItemsChange(showRestricted: boolean): void {
    const showRestrictedItems = showRestricted && this.form.get('standardEnabled').value && this.form.get('enabled').value;

    this.setControlState(['restrictedItemsUrl'], showRestrictedItems && this.isV1orV2);
    this.setControlState(['restrictedItemsText'], showRestrictedItems && this.isV1);
  }

  private onStandardReadyChange(standardReady: boolean): void {
    const isReady = (standardReady || !this.isV1orV2) && this.form.get('enabled').value && this.form.get('standardEnabled').value;
    this.setControlState(['url', 'button'], isReady);
  }

  private onEnabledChange(): void {
    this.onShowRestrictedItemsChange(this.form.get('showRestrictedItems').value);
    this.onPrimaryReadyChange(this.form.get('primaryReady').value);
    this.onStandardReadyChange(this.form.get('standardReady').value);
    this.onStandardEnabledChange(this.form.get('standardEnabled').value);
  }

  private onVersionChange(value: SplitScreenVersion): void {
    const textValidators = [SplitScreenVersion.V3A, SplitScreenVersion.V3B, SplitScreenVersion.V4].includes(value)
      ? [Validators.required]
      : [];

    this.form.get('text').setValidators(textValidators);
    this.form.get('text').updateValueAndValidity();

    this.onStandardEnabledChange(this.form.get('standardEnabled').value);
    this.onStandardReadyChange(this.form.get('standardReady').value);
    this.onPrimaryReadyChange(this.form.get('primaryReady').value);
    this.onShowRestrictedItemsChange(this.form.get('showRestrictedItems').value);
  }

  setBodyOverflow(value: 'hidden' | 'initial') {
    document.body.style.overflow = value;
  }

  getUploadedFileUrl(event: any): void {
    this.form.patchValue({
      logo: event,
      logoOffsetTop: 0,
      logoOffsetLeft: 0,
      logoSize: 0,
      logoOffsetTopMobile: 0,
      logoOffsetLeftMobile: 0,
      logoSizeMobile: 0,
      logoStandardOffsetTop: 0,
      logoStandardOffsetLeft: 0,
      logoStandardSize: 0,
      logoStandardOffsetTopMobile: 0,
      logoStandardOffsetLeftMobile: 0,
      logoStandardSizeMobile: 0,
    });
  }

  dropSection(event: CdkDragDrop<SplitScreenSection[]>) {
    const sections = this.form.get('sections').value;
    moveItemInArray(sections, event.previousIndex, event.currentIndex);
    this.form.patchValue({ sections });
  }

  setDefaultValue(event: Event, field: string, constant: string): void {
    const target = event.target as HTMLInputElement;
    if (target.value?.trim()?.length === 0) {
      this.form.get(field).setValue(SplitScreen.DEFAULT[this.form.get('version').value][constant]);
    }
  }

  onSectionToggle(isVisible: boolean, type: string): void {
    const sections = this.form.get('sections').value;
    const section = sections.find((section: SplitScreenSection) => section.type === type);
    section.isVisible = isVisible;
    this.form.patchValue({ sections });
  }

  toggleSectionExpand(type: string): void {
    const expanded = new Set(this.expandedSections());
    expanded.has(type) ? expanded.delete(type) : expanded.add(type);
    this.expandedSections.set(expanded);
  }

  // --- Split Card section helpers ---

  readonly d = SPLIT_CARD_DEFAULT_OPTIONS;
  private readonly pd = SPLIT_CARD_PRIORITY_DEFAULT_OPTIONS;

  private get splitCardSection(): SplitScreenSection {
    const sections = this.form.get('sections').value;
    return sections.find((s: SplitScreenSection) => s.type === SplitScreenSectionsTypes.splitCard);
  }

  private ensureSplitCardOptions(): Record<string, any> {
    const section = this.splitCardSection;
    if (!section.options) {
      section.options = { ...SPLIT_CARD_DEFAULT_OPTIONS };
    }
    return section.options;
  }

  private emitSections(): void {
    this.form.patchValue({ sections: [...this.form.get('sections').value] });
  }

  onSplitCardOptionChange(field: string, value: string): void {
    const options = this.ensureSplitCardOptions();
    options[field] = value || this.d[field as keyof typeof SPLIT_CARD_DEFAULT_OPTIONS] || '';
    this.emitSections();
  }

  onSplitCardSubToggle(field: string, value: boolean): void {
    const options = this.ensureSplitCardOptions();
    options[field] = value;
    this.emitSections();
  }

  getSplitCardOrder(
    section: SplitScreenSection,
  ): { key: string; label: string; visible: boolean; visibleField: string; title: string; titleField: string }[] {
    const opts = section.options || this.d;
    const order = opts['cardOrder'] || this.d['cardOrder'];
    const cardMap: Record<string, { label: string; visible: boolean; visibleField: string; title: string; titleField: string }> = {
      priority: {
        label: 'Priority card',
        visible: !!opts['priorityCardVisible'],
        visibleField: 'priorityCardVisible',
        title: opts['priorityCardTitle'] || this.d['priorityCardTitle'],
        titleField: 'priorityCardTitle',
      },
      standard: {
        label: 'Standard pickup card',
        visible: !!opts['standardCardVisible'],
        visibleField: 'standardCardVisible',
        title: opts['standardCardTitle'] || this.d['standardCardTitle'],
        titleField: 'standardCardTitle',
      },
    };
    return order.map((key: string) => ({ key, ...cardMap[key] }));
  }

  dropSplitCardSub(event: CdkDragDrop<string[]>): void {
    const options = this.ensureSplitCardOptions();
    const cardOrder = [...(options['cardOrder'] || this.d['cardOrder'])];
    moveItemInArray(cardOrder, event.previousIndex, event.currentIndex);
    options['cardOrder'] = cardOrder;
    this.emitSections();
  }

  getStandardCardBullets(section: SplitScreenSection): string[] {
    return section.options?.['standardCardBullets'] || this.d['standardCardBullets'];
  }

  onStandardBulletChange(index: number, value: string): void {
    const options = this.ensureSplitCardOptions();
    if (!options['standardCardBullets']) {
      options['standardCardBullets'] = [...this.d['standardCardBullets']];
    }
    options['standardCardBullets'][index] = value;
    this.emitSections();
  }

  addStandardBullet(): void {
    const options = this.ensureSplitCardOptions();
    if (!options['standardCardBullets']) {
      options['standardCardBullets'] = [...this.d['standardCardBullets']];
    }
    options['standardCardBullets'].push('');
    this.emitSections();
  }

  removeStandardBullet(index: number): void {
    const options = this.ensureSplitCardOptions();
    if (!options['standardCardBullets']) {
      options['standardCardBullets'] = [...this.d['standardCardBullets']];
    }
    options['standardCardBullets'].splice(index, 1);
    this.emitSections();
  }

  getPriorityCardBullets(section: SplitScreenSection): string[] {
    return section.options?.['priorityCardBullets'] || this.d['priorityCardBullets'];
  }

  isPriorityBulletsDefault(section: SplitScreenSection): boolean {
    const bullets = section.options?.['priorityCardBullets'];
    if (!bullets) return true;
    const defaults = this.pd.bullets;
    return bullets.length === defaults.length && bullets.every((b: string, i: number) => b === defaults[i]);
  }

  onPriorityUseDefaultToggle(useDefault: boolean): void {
    if (useDefault) {
      const options = this.ensureSplitCardOptions();
      delete options['priorityCardBullets'];
      this.emitSections();
    }
  }

  onPriorityBulletChange(index: number, value: string): void {
    const options = this.ensureSplitCardOptions();
    if (!options['priorityCardBullets']) {
      options['priorityCardBullets'] = [...this.pd.bullets];
    }
    options['priorityCardBullets'][index] = value;
    this.emitSections();
  }

  addPriorityBullet(): void {
    const options = this.ensureSplitCardOptions();
    if (!options['priorityCardBullets']) {
      options['priorityCardBullets'] = [...this.pd.bullets];
    }
    options['priorityCardBullets'].push('');
    this.emitSections();
  }

  removePriorityBullet(index: number): void {
    const options = this.ensureSplitCardOptions();
    if (!options['priorityCardBullets']) {
      options['priorityCardBullets'] = [...this.pd.bullets];
    }
    options['priorityCardBullets'].splice(index, 1);
    this.emitSections();
  }

  onFormEnableToggle(event: MatSlideToggleChange) {
    this.form.get('enabled').setValue(event.checked);
    this.enabled.set(event.checked);

    event.checked ? this.form.enable() : this.form.disable();
    event.checked && this.onEnabledChange();
  }

  validateForm(): boolean {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (!this.form.invalid) {
      return true;
    } else {
      return false;
    }
  }
}
